/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type ReactNode, createContext, forwardRef } from 'react';
import {
  type ContextValue,
  DEFAULT_SLOT,
  Provider,
  SearchFieldContext as SearchFieldCoreContext,
  TagGroupContext,
  useContextProps,
} from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { BadgeTagContext } from '../badge-tag/badge-tag.component';
import { BadgeContext } from '../badge/badge.component';
import { GridListItemContext } from '../grid-list-item/grid-list-item.component';
import { GridListContext } from '../grid-list/grid-list.component';
import { ListBoxItemContext } from '../list-box-item/list-box-item.component';
import { ListBoxContext } from '../list-box/list-box.component';
import { ProgressIndicatorContext } from '../progress-indicator/progress-indicator.component';
import { TEXT_BUTTON_SLOTS, TextButtonContext } from '../text-button/text-button.component';
import { TextContext } from '../text/text.component';
import { ViewContext } from '../view/view.component';

interface TagPickerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'style' | 'className'> {
  slot?: string;
  children?: ReactNode;
  style?: stylex.StyleXStyles;
}

const TagPickerContext = createContext<ContextValue<TagPickerProps, HTMLDivElement>>({});

const TagPicker = forwardRef((props: TagPickerProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props] = useContextProps(props, ref, TagPickerContext);

  const { style, children, ...coreProps } = props;

  return (
    <div ref={ref} {...stylex.props(styles.base, style)} {...coreProps}>
      <Provider values={[
        [ViewContext, {
          slots: {
            [DEFAULT_SLOT]: {},
            [TAG_PICKER_SLOTS.INPUT_VIEW]: {
              style: inputViewStyles.base,
            },
          },
        }],
        [SearchFieldCoreContext, { ...stylex.props(searchFieldStyles.base) }],
        [TextContext, {
          slots: {
            [DEFAULT_SLOT]: {},
            [TAG_PICKER_SLOTS.HINT_TEXT]: {
              color: 'tertiary',
              size: 14,
              style: hintStyles.base,
            },
          },
        }],
        [TextButtonContext, {
          slots: {
            [DEFAULT_SLOT]: {},
            [TEXT_BUTTON_SLOTS.LEFT_ICON]: {},
            [TEXT_BUTTON_SLOTS.RIGHT_ICON]: {},
            [TAG_PICKER_SLOTS.CREATE_TEXT_BUTTON]: {
              safeArea: 'none',
              size: 32,
              style: textButtonRowStyles.base,
              variant: 'tertiary',
            },
            [TAG_PICKER_SLOTS.EDIT_TEXT_BUTTON]: {
              safeArea: 'none',
              size: 32,
              style: textButtonRowStyles.base,
              variant: 'tertiary',
            },
          },
        }],
        [ListBoxContext, { style: listStyles.base }],
        [ListBoxItemContext, { size: 40, style: listItemStyles.base }],
        [GridListContext, { style: listStyles.base }],
        [GridListItemContext, { size: 40, style: listItemStyles.base }],
        [TagGroupContext, { ...stylex.props(tagGroupStyles.base) }],
        [BadgeContext, { style: badgeStyles.base }],
      ]}>
        <Provider values={[
          [BadgeTagContext, { style: badgeStyles.base }],
          [ProgressIndicatorContext, { size: 12 }],
        ]}>
          {children}
        </Provider>
      </Provider>
    </div>
  );
});

const hintStyles = stylex.create({
  base: {
    padding: 8,
  },
});

const inputViewStyles = stylex.create({
  base: {
    gap: 6,
    paddingInline: 8,
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexWrap: 'wrap',
    borderBottomColor: $.border06,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    paddingBottom: 12,
  },
});

const listItemStyles = stylex.create({
  base: {
    borderRadius: 6,
    marginInline: 0,
    paddingBlock: 6,
    paddingInline: 8,
    height: 'auto',
    minHeight: 32,
  },
});

const listStyles = stylex.create({
  base: {
    outline: 'none',
    overflow: 'auto',
    paddingBlock: 0,
    boxSizing: 'border-box',
    maxHeight: 'inherit',
    scrollbarWidth: 'none'
  },
});

// SearchField overwrites InputContext internally before Input reads it, so we cannot inject
// Input styles via context. Instead, apply a descendant selector on SearchField's own element.
const searchFieldStyles = stylex.create({
  base: {
    // eslint-disable-next-line @stylexjs/valid-styles
    ':not(#__unused__) > .react-aria-Input': {
      borderStyle: 'none',
      outline: 'none',
      backgroundColor: 'transparent',
      caretColor: $.textInteractive,
      color: $.textPrimary,
      fontFamily: 'Inter',
      fontSize: 14,
      letterSpacing: -0.45,
      minWidth: 0,
      width: '100%',
      '::placeholder': { color: $.textPlaceholder },
    },
    flex: '1 1 25%',
    minWidth: 0,
  },
});

// TagGroup overwrites TagListContext internally before TagList reads it, so we cannot inject
// TagList styles via context. Instead, apply a descendant selector on TagGroup's own element.
const tagGroupStyles = stylex.create({
  base: {
    // There is no other way to style TagList from TagGroup — RAC overwrites TagListContext internally.
    // eslint-disable-next-line @stylexjs/valid-styles
    ':not(#__unused__) > .react-aria-TagList': {
      gap: 6,
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
    },
    minWidth: 0,
    width: '100%',
  },
});

const textButtonRowStyles = stylex.create({
  base: {
    borderRadius: 6,
    overflow: 'hidden',
    paddingBlock: 6,
    paddingInline: 8,
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered])': $.opacity04,
    },
    justifyContent: 'flex-start',
    minHeight: 32,
    width: '100%',
  },
});

const styles = stylex.create({
  base: {
    overflow: 'hidden',
    paddingInline: 4,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 6,
    paddingTop: 12,
    minWidth: 232,
    maxHeight: 226,
    padding: 4,
    borderColor: $.border08,
    borderRadius: 10,
    borderStyle: 'solid',
    borderWidth: 1,
    outline: 'none',
    backgroundColor: $.surfaceBGPrimary,
    boxShadow: $.shadowBottom060Medium,
  },
});

const badgeStyles = stylex.create({
  base: {
    overflow: 'hidden',
    flexShrink: 1,
    minWidth: 0,
  },
});

const TAG_PICKER_SLOTS = {
  CREATE_TEXT_BUTTON: 'create-text-button',
  EDIT_TEXT_BUTTON: 'edit-text-button',
  HINT_TEXT: 'hint-text',
  INPUT_VIEW: 'input-view',
};

export { TAG_PICKER_SLOTS, TagPicker, TagPickerContext, type TagPickerProps };
